<template>
    <div>
        <v-card solo flat>
            <v-card-title>
                <v-spacer />
                <v-btn color="primary" @click="openDialog">Add Model</v-btn>
            </v-card-title>
            <v-card-text>
                <v-data-table
                    :headers="headers"
                    :items="models"
                    class="elevation-1"
                    :loading="loading"
                >
                    <template v-slot:[`item.file`]="{ item }">
                        <span
                            style="text-decoration: underline; color: blue; cursor: pointer;"
                            @click="openModelFile(item)"
                            >{{ item.file.name }}</span
                        >
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <div class="d-flex justify-center">
                            <v-btn icon @click="editModel(item)">
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon @click="deleteModel(item)">
                                <v-icon>mdi-delete</v-icon>
                            </v-btn>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
        <v-dialog v-model="dialog" max-width="400px">
            <v-card :loading="loading">
                <v-card-title>{{
                    editMode ? 'Edit Model' : 'Add Model'
                }}</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                v-model="model.name"
                                label="Name"
                                :rules="[rules.required]"
                                required
                                prepend-icon="mdi-text-box-outline"
                                :items="types"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-file-input
                                label="File"
                                v-model="modelFile"
                                prepend-icon="mdi-upload"
                                :clearable="false"
                            ></v-file-input>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="closeDialog" color="red">Cancel</v-btn>
                    <v-btn color="primary" @click="saveModel" :loading="loading"
                        >Save</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import { v4 as uuidv4 } from 'uuid'
import { mapMutations } from 'vuex'
import { saveFile } from '@/services/storage/saveFile.js'
import API from '@/services/api'
import { deleteFile } from '@/services/storage/deleteFile.js'
import { storage } from '@/services/firebase'
import _ from 'lodash'

export default {
    name: 'ACSFiles',
    props: {
        setting: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        loading: false,
        headers: [
            { text: 'Name', value: 'name', sortable: false },
            { text: 'File', value: 'file', sortable: false },
            {
                text: 'Actions',
                value: 'actions',
                sortable: false,
                width: '100px',
                align: 'center',
            },
        ],
        models: [],
        model: {},
        dialog: false,
        editMode: false,
        rules: {
            required: v => !!v || 'The value is required',
        },
        types: [
            { text: 'HPL CLADDING', value: 'HPL PANELS' },
            { text: 'ALUMINUM CLADDING', value: 'ALUMINUM CLADDING' },
        ],
        companyId: JSON.parse(localStorage.getItem('company')),
        modelFile: null,
    }),
    async mounted() {
        try {
            this.loading = true
            await this.getModels()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        // Helper method to safely extract file extension
        getFileExtension(filename) {
            if (!filename) return ''
            const lastDot = filename.lastIndexOf('.')
            return lastDot !== -1 ? filename.substring(lastDot) : ''
        },
        editModel(item) {
            this.model = _.cloneDeep(item)
            this.editMode = true
            this.dialog = true
        },
        async deleteModel(item) {
            try {
                this.loading = true
                await deleteFile(
                    item.file.id,
                    `${this.companyId}/settings/client_portal/models/${item.name}`
                )
                await API.deleteModel({
                    settingId: this.setting.id,
                    id: item.id,
                })
                this.models = this.models.filter(m => m.id != item.id)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        openDialog() {
            this.dialog = true
        },
        closeDialog() {
            this.dialog = false
            this.editMode = false
            this.model = {}
            this.modelFile = null
        },
        async saveModel() {
            try {
                this.loading = true
                if (this.editMode) {
                    let file = undefined
                    if (this.modelFile) {
                        const ext = this.getFileExtension(this.modelFile.name)
                        file = {
                            id: uuidv4() + ext,
                            name: this.modelFile.name,
                        }
                        // save file in firebase storage
                        await saveFile(
                            this.modelFile,
                            `${this.companyId}/settings/client_portal/models/${this.model.name}`,
                            file.id
                        )
                    }
                    // save in db
                    const updatedModel = await API.updateModel({
                        settingId: this.setting.id,
                        id: this.model.id,
                        name: this.model.name,
                        ...(file ? { file } : {}),
                        modelType: 'ACS',
                    })
                    const index = this.models.findIndex(
                        item => item.id == this.model.id
                    )
                    this.$set(this.models, index, {
                        ...this.models[index],
                        ...updatedModel,
                    })
                } else {
                    if (this.models.some(m => m.name == this.model.name)) {
                        throw new Error('The model already exists')
                    }
                    const ext = this.getFileExtension(this.modelFile.name)
                    const file = {
                        id: uuidv4() + ext,
                        name: this.modelFile.name,
                    }
                    // save file in firebase storage
                    await saveFile(
                        this.modelFile,
                        `${this.companyId}/settings/client_portal/models/${this.model.name}`,
                        file.id
                    )
                    // save in db
                    const newModel = await API.createModel({
                        settingId: this.setting.id,
                        name: this.model.name,
                        file,
                        modelType: 'ACS',
                    })
                    this.models.push(newModel)
                }
                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async downloadFileWithName(filePath, fileId, filename) {
            // Get download URL from Firebase Storage
            const storageRef = storage().ref(filePath)
            const documentRef = storageRef.child(fileId)
            const downloadURL = await documentRef.getDownloadURL()

            // Download file with custom name
            const response = await fetch(downloadURL)
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const link = window.document.createElement('a')
            link.href = url
            link.download = filename
            window.document.body.appendChild(link)
            link.click()
            window.document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        },
        async openModelFile(item) {
            try {
                this.loading = true
                const filePath = `${this.companyId}/settings/client_portal/models/${item.name}`
                const filename = item.file.name || 'datasheet'
                await this.downloadFileWithName(
                    filePath,
                    item.file.id,
                    filename
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async getModels() {
            try {
                this.loading = true
                const allModels = await API.getModels({
                    settingId: this.setting.id,
                })
                // Filtrar solo modelos con modelType === "ACS"
                this.models = allModels.filter(
                    model => model.modelType === 'ACS'
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

